import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/gallery.css";
import { MasonryPhotoAlbum } from "react-photo-album";
import "react-photo-album/masonry.css";
import gal from "../images/gall.jpg";
import { LazyLoadImage } from "react-lazy-load-image-component";
import metaData from "./metaData";
import { useScrollAnimation } from "../utils/useScrollAnimation";

const BATCH_SIZE = 50; // Load 50 images at a time
const LOADING_TIMEOUT = 8000; // 8 seconds before showing error

function Gallery() {
  const allPhotoes = metaData.galleryImages || [];
  const [displayedPhotos, setDisplayedPhotos] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingImages, setLoadingImages] = useState(new Set());
  const [failedImages, setFailedImages] = useState(new Set());
  const observerTarget = useRef(null);
  const introRef = useScrollAnimation();
  const gridRef = useScrollAnimation();
  const timeoutRefs = useRef(new Map());

  // Initial load - first batch
  useEffect(() => {
    setDisplayedPhotos(allPhotoes.slice(0, BATCH_SIZE));
    setHasMore(allPhotoes.length > BATCH_SIZE);
  }, [allPhotoes]);

  // Load more images when user scrolls to bottom
  const loadMore = useCallback(() => {
    setDisplayedPhotos((prev) => {
      const newCount = prev.length + BATCH_SIZE;
      const newPhotos = allPhotoes.slice(0, newCount);
      setHasMore(newCount < allPhotoes.length);
      return newPhotos;
    });
  }, [allPhotoes]);

  // Handle image load
  const handleImageLoad = useCallback((imageSrc) => {
    setLoadingImages((prev) => {
      const updated = new Set(prev);
      updated.delete(imageSrc);
      return updated;
    });
    // Clear timeout if exists
    if (timeoutRefs.current.has(imageSrc)) {
      clearTimeout(timeoutRefs.current.get(imageSrc));
      timeoutRefs.current.delete(imageSrc);
    }
  }, []);

  // Handle image error
  const handleImageError = useCallback((imageSrc) => {
    setFailedImages((prev) => new Set(prev).add(imageSrc));
    setLoadingImages((prev) => {
      const updated = new Set(prev);
      updated.delete(imageSrc);
      return updated;
    });
  }, []);

  // Prevent right-click on images
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    return false;
  }, []);

  // Custom render function for images with protection
  const renderPhoto = useCallback(
    ({ photo, wrapperStyle, index }) => {
      const imageSrc = photo.src;
      const isLoading = loadingImages.has(imageSrc);
      const isFailed = failedImages.has(imageSrc);

      return (
        <div
          key={index}
          style={wrapperStyle}
          className="gallery-photo-wrapper"
          onContextMenu={handleContextMenu}
        >
          {isLoading && <div className="image-loader"></div>}
          {isFailed && <div className="image-error">Failed to load</div>}
          <img
            alt="gallery-photo"
            src={imageSrc}
            loading="lazy"
            style={{
              opacity: isFailed ? 0 : isLoading ? 0 : 1,
              transition: "opacity 0.3s ease-in-out",
              userSelect: "none",
              WebkitUserSelect: "none",
              draggable: false,
            }}
            onLoad={() => handleImageLoad(imageSrc)}
            onError={() => handleImageError(imageSrc)}
            onDragStart={(e) => e.preventDefault()}
            onContextMenu={handleContextMenu}
          />
        </div>
      );
    },
    [loadingImages, failedImages, handleImageLoad, handleImageError, handleContextMenu]
  );

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loadMore]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  return (
    <>
    <div id="gallery" className="gallery-page">
      <header className="gallery-hero">
        <LazyLoadImage className="gallery-hero-img" src={gal} alt="" />
        <div className="gallery-hero-scrim" aria-hidden="true" />
        <div className="gallery-hero-inner">
          <h1 className="gallery-hero-title">Images</h1>
        </div>
      </header>

      <div className="gallery-intro plan-des section-lead scroll-animate" ref={introRef}>
        <div>
          We, WEDDING STORIES, specialize in capturing the special moments of
          your big day in a way that will last a lifetime. Our team of
          experienced photographers understands the importance of your wedding
          day and strives to create images that reflect the beauty, joy, and
          love that you and your partner share.
        </div>
      </div>

      <div className="gallery-grid-wrap scroll-animate" ref={gridRef}>
        <MasonryPhotoAlbum 
          photos={displayedPhotos}
          targetRowHeight={300}
          spacing={12}
          columns={4}
          renderPhoto={renderPhoto}
          componentsProps={{
            imageProps: {
              loading: "lazy",
            }
          }}
        />
        {hasMore && <div ref={observerTarget} style={{ height: "20px", marginTop: "20px" }} />}
        {!hasMore && displayedPhotos.length > 0 && (
          <p style={{ textAlign: "center", marginTop: "30px", color: "#999" }}>
            All {displayedPhotos.length} images loaded
          </p>
        )}
      </div>
    </div>
    </>
  );
}

export default Gallery;
